/**
 * Cég váltás oldal — fehér háttér, mint a bejelentkezés.
 * Mutatja az aktív tenant-et (kiemelve) és az összes többi céget, ahova a user tartozik.
 * Beépített "Új cég hozzáadása" form (authenticated, nem kell kijelentkezni).
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { post } from '../../lib/api';

export default function TenantSwitcherPage() {
  const { user, myTenants, loadMyTenants, switchTenant, createCompany } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState<string | null>(null);
  const [notifCounts, setNotifCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState('');

  // Új cég form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newTaxNumber, setNewTaxNumber] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupDone, setLookupDone] = useState(false);
  const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadMyTenants()
      .catch(() => setError('Nem sikerült betölteni a céglistát.'))
      .finally(() => setLoading(false));
  }, [loadMyTenants]);

  useEffect(() => {
    const sync = () => setNotifCounts((window as any).__modulumNotifCounts || {});
    sync();
    const t = setInterval(sync, 5000);
    return () => clearInterval(t);
  }, []);

  const handleSwitch = async (tenantId: string) => {
    if (switching || tenantId === user?.tenantId) return;
    setSwitching(tenantId);
    setError('');
    try {
      await switchTenant(tenantId);
      window.location.replace('/dashboard');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message || 'Hiba a cégváltás során. Kérjük próbálja újra.');
      setSwitching(null);
    }
  };

  const handleNavLookup = async (taxNum: string) => {
    const digits = taxNum.replace(/-/g, '').slice(0, 8);
    if (digits.length < 8) return;
    setLookupLoading(true);
    setLookupError('');
    setLookupDone(false);
    try {
      const data = await post<{ found: boolean; message?: string; data?: Record<string, unknown> }>('/partners/lookup-tax-number', { taxNumber: digits });
      if (!data.found) {
        setLookupError(data.message || 'Az adószám a NAV nyilvántartásában nem található.');
      } else {
        const d = data.data || {};
        const name = (d.name || d.taxpayerName || '') as string;
        if (name) setNewCompanyName(name);
        const fullTax = (d.taxNumber || '') as string;
        if (fullTax) setNewTaxNumber(fullTax);
        // Onboarding előtöltéshez sessionStorage-ba mentjük
        sessionStorage.setItem('nav_prefill', JSON.stringify(d));
        setLookupDone(true);
      }
    } catch {
      setLookupError('NAV lekérdezés sikertelen. Töltsd ki manuálisan.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleTaxNumberChange = (val: string) => {
    setNewTaxNumber(val);
    setLookupDone(false);
    setLookupError('');
    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    const digits = val.replace(/-/g, '');
    if (digits.length === 8) {
      lookupTimerRef.current = setTimeout(() => handleNavLookup(val), 500);
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompanyName.trim() || creating) return;
    setCreating(true);
    setCreateError('');
    try {
      await createCompany(newCompanyName.trim(), newTaxNumber.trim() || undefined);
      window.location.replace('/onboarding/company');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setCreateError(e?.message || 'Hiba az új cég létrehozásakor. Kérjük próbálja újra.');
      setCreating(false);
    }
  };

  return (
    <div className="auth-bg">
      <div className="auth-card" style={{ maxWidth: 440 }}>
        {/* Fejléc */}
        <div className="auth-head">
          <div className="auth-logo">
            <svg width="32" height="32" viewBox="0 0 100 100">
              <path d="M16 88 L16 40 L50 64" fill="none" stroke="#14233F" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M50 64 L84 16 L84 64" fill="none" stroke="#F47B20" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Modulum</span>
          </div>
          <p className="auth-sub">Válassz céget a folytatáshoz</p>
        </div>

        {/* Bejelentkezett felhasználó */}
        <div style={{ paddingBottom: 16, marginBottom: 4 }}>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>
            Bejelentkezve
          </div>
          <div style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
            {user?.name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({user?.email})</span>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', marginBottom: 16 }} />

        {/* Cég lista */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 14 }}>
            Betöltés...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(myTenants ?? []).map(tenant => {
              const isActive = tenant.id === user?.tenantId;
              const isSwitching = switching === tenant.id;
              return (
                <button
                  key={tenant.id}
                  onClick={() => isActive ? navigate('/dashboard') : handleSwitch(tenant.id)}
                  disabled={!!switching || creating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 16px',
                    borderRadius: 10,
                    border: isActive ? '2px solid #14233F' : '1.5px solid #e5e7eb',
                    background: isActive ? '#f0f4ff' : '#fff',
                    cursor: (switching || creating) ? 'not-allowed' : 'pointer',
                    opacity: (switching && !isSwitching) || creating ? 0.5 : 1,
                    textAlign: 'left',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  {/* Avatar + badge */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: isActive ? '#14233F' : '#e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 700, color: isActive ? '#fff' : '#6b7280',
                    }}>
                      {tenant.name.charAt(0).toUpperCase()}
                    </div>
                    {(notifCounts[tenant.id] || 0) > 0 && (
                      <div style={{
                        position: 'absolute', top: -5, right: -5,
                        background: '#DC2626', color: '#fff',
                        borderRadius: '50%', fontSize: 9, fontWeight: 700,
                        minWidth: 16, height: 16, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #fff', lineHeight: 1,
                      }}>
                        {notifCounts[tenant.id] > 9 ? '9+' : notifCounts[tenant.id]}
                      </div>
                    )}
                  </div>

                  {/* Cég neve */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                      {tenant.name}
                    </div>
                    {isActive && (
                      <div style={{ fontSize: 11, color: '#14233F', fontWeight: 600, marginTop: 2 }}>
                        Aktív munkakörnyezet
                      </div>
                    )}
                  </div>

                  {/* Jelző */}
                  <div style={{ flexShrink: 0 }}>
                    {isSwitching ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2.5"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="#14233F" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    ) : isActive ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#14233F"/>
                        <path d="M7 12l3.5 3.5L17 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18l6-6-6-6" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <div className="auth-error" style={{ marginTop: 12 }}>{error}</div>
        )}

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 16 }}>
          {/* Új cég gomb / form */}
          {!showNewForm ? (
            <button
              onClick={() => setShowNewForm(true)}
              disabled={!!switching || creating}
              style={{
                width: '100%', background: 'transparent',
                border: '1.5px dashed #d1d5db', borderRadius: 10,
                padding: '12px 16px', fontSize: 14, cursor: 'pointer',
                color: '#6b7280', fontWeight: 500, textAlign: 'center',
                transition: 'border-color 0.15s, color 0.15s',
              }}
            >
              + Új cég hozzáadása
            </button>
          ) : (
            <form onSubmit={handleCreateCompany} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                Új cég adatai
              </div>

              {/* Adószám — NAV auto-lookup */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>
                  Adószám <span style={{ color: '#9ca3af', fontWeight: 400 }}>(8 jegy után auto-kitölt)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type="text"
                    value={newTaxNumber}
                    onChange={e => handleTaxNumberChange(e.target.value)}
                    placeholder="12345678-1-23"
                    disabled={creating}
                    autoFocus
                    style={{ paddingRight: lookupLoading ? 36 : undefined }}
                  />
                  {lookupLoading && (
                    <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="2.5"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="#14233F" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  )}
                  {lookupDone && !lookupLoading && (
                    <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#16a34a', fontSize: 16 }}>✓</div>
                  )}
                </div>
                {lookupError && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{lookupError} — <button type="button" onClick={() => { setLookupError(''); setShowNewForm(true); }} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>manuális kitöltés</button></div>
                )}
              </div>

              {/* Cégnév */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>
                  Cégnév *
                  {lookupDone && <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 400 }}>NAV-ból kitöltve</span>}
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={newCompanyName}
                  onChange={e => setNewCompanyName(e.target.value)}
                  placeholder="Pl. Minta Kft."
                  required
                  disabled={creating}
                />
              </div>

              {createError && (
                <div className="auth-error">{createError}</div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewForm(false);
                    setNewCompanyName(''); setNewTaxNumber('');
                    setCreateError(''); setLookupError(''); setLookupDone(false);
                  }}
                  disabled={creating}
                  style={{
                    flex: 1, background: 'transparent', border: '1.5px solid #e5e7eb',
                    borderRadius: 8, padding: '10px', fontSize: 13, cursor: 'pointer', color: '#6b7280',
                  }}
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={creating || !newCompanyName.trim()}
                  className="btn btn-primary"
                  style={{ flex: 2, padding: '10px', fontSize: 13, opacity: (creating || !newCompanyName.trim()) ? 0.6 : 1 }}
                >
                  {creating ? 'Létrehozás...' : 'Cég létrehozása'}
                </button>
              </div>
            </form>
          )}

          {/* Visszalépés */}
          <button
            onClick={() => navigate('/dashboard')}
            disabled={!!switching || creating}
            style={{
              width: '100%', marginTop: 10,
              background: 'transparent', border: 'none',
              padding: '8px', fontSize: 13,
              cursor: (switching || creating) ? 'not-allowed' : 'pointer',
              color: '#9ca3af',
            }}
          >
            Visszalépés
          </button>
        </div>
      </div>
    </div>
  );
}
